import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem;
`;

const LoginCard = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
  font-weight: 700;
  color: #111;
`;

const Description = styled.p`
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const LoginButton = styled.button`
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  background-color: #4285F4;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #357ae8;
  }
  
  svg {
    margin-right: 10px;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  margin-top: 2rem;
  cursor: pointer;
  font-size: 0.9rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');

  // Set the mode based on location state or query param (?mode=signup)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryMode = params.get('mode');
    if (queryMode === 'signup' || queryMode === 'signin') {
      setMode(queryMode);
    } else if (location.state && location.state.mode) {
      setMode(location.state.mode);
    }
  }, [location.state, location.search]);

  // Google OAuth login real
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Get user info from Google
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        });
        
        const userInfo = await userInfoResponse.json();
        
        // Create user object
        const user = {
          id: userInfo.id,
          name: userInfo.name,
          email: userInfo.email,
          profilePicture: userInfo.picture,
          googleId: userInfo.id
        };
        
        await login(user);
        
        // Redirect user back to where they came from or home
        navigate(-1);
        
      } catch (error) {
        console.error('Error during Google login:', error);
        // Fallback to mock user for development
        const mockUser = {
          id: 'user-' + Math.random().toString(36).substr(2, 9),
          name: 'Usuario de Canvasoft',
          email: 'usuario@example.com',
          profilePicture: 'https://picsum.photos/id/237/200/200'
        };
        login(mockUser);
        navigate(-1);
      }
    },
    onError: () => {
      console.error('Google login failed');
    },
  });

  const handleBackClick = () => {
    navigate(-1);
  };

  const getTitle = () => {
    return mode === 'signin' ? 'Sign in to your account' : 'Create a new account';
  };

  const getDescription = () => {
    return mode === 'signin' 
      ? 'Sign in with your Google account to save your favorite images and make purchases.'
      : 'Create a new account with Google to start collecting your favorite AI-generated images.';
  };

  return (
    <LoginContainer>
      <LoginCard>
        <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" style={{marginBottom: '0.4rem'}}>
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
        <Description>
          {getDescription()}
        </Description>
        
        <LoginButton onClick={() => googleLogin()}>
          {mode === 'signin' ? 'Sign in with Google' : 'Sign up with Google'}
        </LoginButton>
        
        <BackButton onClick={handleBackClick}>
          Return without {mode === 'signin' ? 'signing in' : 'signing up'}
        </BackButton>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;